/**
 * YogSeoMeta.js
 *
 * SEO + structured data components for the Yog (Online Yoga) section.
 *
 * FIX NOTES (debug mode):
 *   1. Added unique `key` prop to <Helmet> so React always re-renders on change
 *   2. Fixed canonical URL logic — no more duplicate segments when groupName
 *      is undefined and falls back to videoName
 *   3. VideoSchemaMeta now also has a keyed <Helmet> so it updates on video change
 *   4. Description capped correctly at 155 chars with word boundary trimming
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Constants ─────────────────────────────────────────────────────────────────
const SITE_NAME      = "Yoga Shastra";
const SITE_URL       = "https://www.yoga-shastra.com";
const OG_IMAGE       = `${SITE_URL}/og-image.jpg`;
const TWITTER_HANDLE = "@yogashastra";

// ─── Slug helper ───────────────────────────────────────────────────────────────
const toSlug = (str = "") =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// ─── Safe description trimmer — respects word boundaries ──────────────────────
const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

// ═══════════════════════════════════════════════════════════════════════════════
// YogPageMeta
// ═══════════════════════════════════════════════════════════════════════════════

export const YogPageMeta = ({
  categoryName,
  groupName,
  videoName,
  videoDesc,
}) => {
  // ── Build dynamic title / description / canonical ────────────────────────────
  let title       = `Online Yoga Styles & Holistic Practices – ${SITE_NAME}`;
  let description = "Discover yoga styles at Yoga Shastra — from immunity-boosting practices to chronic illness management. Join guided sessions online from anywhere.";
  let canonical   = `${SITE_URL}/online-yoga`;

  if (categoryName) {
    title       = `${categoryName} Yoga Online | Guided Practices – ${SITE_NAME}`;
    description = `Explore ${categoryName} yoga practices at Yoga Shastra. Expert-guided online sessions for all levels from the comfort of your home.`;
    canonical   = `${SITE_URL}/online-yoga/${toSlug(categoryName)}`;
  }

  if (groupName) {
    title       = `${groupName} | ${categoryName} Yoga – ${SITE_NAME}`;
    description = `Learn ${groupName} with Yoga Shastra's guided ${categoryName} yoga sessions. Step-by-step instructions, benefits, and precautions.`;
    canonical   = `${SITE_URL}/online-yoga/${toSlug(categoryName)}/${toSlug(groupName)}`;
  }

  if (videoName) {
    title       = `${videoName} | ${categoryName} Yoga – ${SITE_NAME}`;
    description = videoDesc
      ? trimDesc(videoDesc)
      : `Watch ${videoName} — a guided ${categoryName} yoga practice with instructions, benefits, and precautions from Yoga Shastra.`;

    // FIX: only append video slug if it differs from groupName to avoid
    // duplicate path segments like /anulom-vilom/anulom-vilom
    const groupSlug = groupName ? toSlug(groupName) : null;
    const videoSlug = toSlug(videoName);
    canonical = groupSlug && groupSlug !== videoSlug
      ? `${SITE_URL}/online-yoga/${toSlug(categoryName)}/${groupSlug}/${videoSlug}`
      : `${SITE_URL}/online-yoga/${toSlug(categoryName)}/${videoSlug}`;
  }

  // ── BreadcrumbList JSON-LD ───────────────────────────────────────────────────
  const breadcrumbItems = [
    { name: "Home",        url: `${SITE_URL}/` },
    { name: "Online Yoga", url: `${SITE_URL}/online-yoga` },
  ];
  if (categoryName) {
    breadcrumbItems.push({
      name: categoryName,
      url:  `${SITE_URL}/online-yoga/${toSlug(categoryName)}`,
    });
  }
  if (groupName && groupName !== categoryName) {
    breadcrumbItems.push({
      name: groupName,
      url:  `${SITE_URL}/online-yoga/${toSlug(categoryName)}/${toSlug(groupName)}`,
    });
  }
  if (videoName) {
    breadcrumbItems.push({ name: videoName, url: canonical });
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems.map((item, i) => ({
      "@type":    "ListItem",
      position:   i + 1,
      name:       item.name,
      item:       item.url,
    })),
  };

  // FIX: unique key forces Helmet to flush updates when canonical changes
  return (
    <Helmet key={canonical}>
      {/* ── Primary meta ── */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* ── Open Graph ── */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={canonical} />
      <meta property="og:image"        content={OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_IN" />

      {/* ── Twitter card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* ── BreadcrumbList structured data ── */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// VideoSchemaMeta
// ═══════════════════════════════════════════════════════════════════════════════

const toIsoDuration = (str = "") => {
  if (!str) return "PT10M";
  const hours   = str.match(/(\d+)\s*hr/i)?.[1];
  const minutes = str.match(/(\d+)\s*min/i)?.[1];
  if (!hours && !minutes) return "PT10M";
  return `PT${hours ? hours + "H" : ""}${minutes ? minutes + "M" : ""}`;
};

export const VideoSchemaMeta = ({
  videoId,
  videoName,
  description,
  duration,
  category,
  uploadDate = "2023-01-01",
}) => {
  if (!videoId || !videoName) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type":    "VideoObject",
    name:        videoName,
    description: description || `Guided ${category ?? "yoga"} practice: ${videoName}. Expert instruction from Yoga Shastra.`,
    thumbnailUrl: [
      `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
      `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    ],
    uploadDate,
    duration:    toIsoDuration(duration),
    embedUrl:    `https://www.youtube.com/embed/${videoId}`,
    contentUrl:  `https://www.youtube.com/watch?v=${videoId}`,
    publisher: {
      "@type": "Organization",
      name:    SITE_NAME,
      url:     SITE_URL,
      logo: {
        "@type": "ImageObject",
        url:     `${SITE_URL}/logo.png`,
      },
    },
    inLanguage:       "en-IN",
    isFamilyFriendly: true,
    genre:            category ?? "Yoga",
    keywords:         `yoga, ${category ?? ""}, online yoga, yoga shastra, guided yoga`.replace(/,\s*,/, ","),
  };

  // FIX: key on videoId so Helmet re-injects the script tag on every video change
  return (
    <Helmet key={`video-schema-${videoId}`}>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};